'use client'

import { Button, Cell, Dialog, Image, Spacer, Text } from '@vinted/web-ui'

import { useAbTest, useTrackAbTest } from '@marketplace-web/shared/ab-tests'
import { useIsDarkMode } from '@marketplace-web/shared/dark-mode'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ScrollableArea } from '@marketplace-web/shared/ui-helpers'
import { useAsset } from '@marketplace-web/domain/assets'

type Props = {
  isOpen: boolean
  onSubmit: () => void
  onClose: () => void
}

const ConfirmDeliveredEverythingIsOkModal = ({ isOpen, onSubmit, onClose }: Props) => {
  const translate = useTranslate('conversation.confirm_delivered_everything_is_ok_modal')
  const isDarkMode = useIsDarkMode()
  const everythingIsOkImageChangeAbTest = useAbTest('everything_ok_image_change')
  useTrackAbTest(everythingIsOkImageChangeAbTest, isOpen)

  const asset = useAsset(`/assets/complete-delivered-transaction${isDarkMode ? '/dark' : ''}`)
  const coatCheckImagePath = asset('coat-check.svg')
  const generalCheckImagePath = asset('general-check.svg')
  const imagePath =
    everythingIsOkImageChangeAbTest?.variant === 'on' ? generalCheckImagePath : coatCheckImagePath

  const renderBody = () => {
    return (
      <Cell>
        <div className="u-text-center">
          <Image role="img" src={imagePath} />
        </div>
        <Text
          as="h1"
          width={Text.Width.Parent}
          alignment={Text.Alignment.Center}
          type={Text.Type.Heading}
          text={translate('title_complete_order')}
          html
        />
        <Spacer size={Spacer.Size.Large} />
        <Text as="span" text={translate('body_complete_order')} html />
      </Cell>
    )
  }

  const renderActions = () => {
    return (
      <Cell>
        <Button
          text={translate('actions.confirm_complete_order')}
          onClick={onSubmit}
          styling={Button.Styling.Filled}
          testId="confirm-everything-is-ok-modal--confirm-button"
        />
        <Spacer size={Spacer.Size.Medium} />
        <Button
          text={translate('actions.close')}
          onClick={onClose}
          testId="confirm-everything-is-ok-modal--close-button"
          styling={Button.Styling.Flat}
        />
      </Cell>
    )
  }

  return (
    <Dialog hasScrollableContent testId="confirm-everything-is-ok-modal" show={isOpen}>
      <ScrollableArea>
        {renderBody()}
        {renderActions()}
      </ScrollableArea>
    </Dialog>
  )
}

export default ConfirmDeliveredEverythingIsOkModal
